/**
 * Admin-Tagesbericht — Klartext-Report in einfacher Sprache, generiert
 * vom Backend (`/api/admin/analytics/day-report`). Der Nutzer wählt ein
 * Datum, klickt „Bericht erstellen", und bekommt eine editoriale
 * Story-Ansicht des Tages ohne Fachbegriffe.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FileText, Calendar, ExternalLink } from "lucide-react";
import { api } from "@/lib/auth";
import { Link } from "react-router-dom";

type DayReport = {
    date: string;
    date_pretty: string;
    total_humans: number;
    summary: string;
    highlights: string[];
    interesting: { heading: string; story: string; clock: string }[];
    short_visits_summary: string | null;
    form_submissions_count: number;
    closing_lines: string[];
    mood: "ruhig" | "solide" | "gut" | "sehr gut";
    real_viewer_stats?: {
        total: number;
        video360: number;
        photo360: number;
        before_after_360: number;
    };
    real_viewer_note?: string;
};

function today(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function yesterday(): string {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export default function AdminTagesbericht() {
    const [date, setDate] = useState<string>(today());
    const [report, setReport] = useState<DayReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (d: string) => {
        setLoading(true);
        setError(null);
        setReport(null);
        const res = await api<DayReport>(
            `/api/admin/analytics/day-report?date=${encodeURIComponent(d)}`,
        );
        setLoading(false);
        if (res.ok && res.data) setReport(res.data);
        else setError(res.error || "Bericht konnte nicht geladen werden.");
    }, []);

    useEffect(() => {
        void load(date);
        // Nur beim ersten Rendern automatisch laden. Weiterer Wechsel via Button.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const moodColor = useMemo<string>(() => {
        if (!report) return "hsl(var(--hf-muted))";
        return report.mood === "sehr gut"
            ? "#7A5A1D"
            : report.mood === "gut"
                ? "#9A7434"
                : report.mood === "solide"
                    ? "hsl(var(--hf-ink))"
                    : "hsl(var(--hf-muted))";
    }, [report]);

    return (
        <div className="max-w-3xl mx-auto py-8 px-4 md:px-0">
            <Helmet>
                <title>Tagesbericht · HypeFly Admin</title>
            </Helmet>

            <header className="mb-8 flex items-center gap-3">
                <FileText size={28} strokeWidth={1.5} className="text-hf-ink" />
                <div>
                    <h1 className="font-display text-2xl md:text-3xl text-hf-ink">Tagesbericht</h1>
                    <p className="text-hf-muted text-sm mt-1">
                        Ein Kurz-Bericht Ihres Tages in einfacher Sprache — ohne Fachbegriffe.
                    </p>
                </div>
            </header>

            {/* Datumsauswahl */}
            <div
                className="mb-8 p-4 rounded-sm flex flex-wrap items-center gap-3"
                style={{ background: "hsl(var(--hf-surface))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <label className="flex items-center gap-2 text-sm text-hf-ink">
                    <Calendar size={16} strokeWidth={1.8} />
                    Datum:
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        max={today()}
                        className="px-2 py-1 rounded-sm text-sm font-mono"
                        style={{ background: "white", border: "1px solid hsl(var(--hf-line))" }}
                        data-testid="tagesbericht-date-input"
                    />
                </label>
                <button
                    type="button"
                    onClick={() => setDate(today())}
                    className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-sm"
                    style={{ background: "transparent", color: "hsl(var(--hf-muted))", border: "1px solid hsl(var(--hf-line))" }}
                    data-testid="tagesbericht-quick-today"
                >
                    Heute
                </button>
                <button
                    type="button"
                    onClick={() => setDate(yesterday())}
                    className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-sm"
                    style={{ background: "transparent", color: "hsl(var(--hf-muted))", border: "1px solid hsl(var(--hf-line))" }}
                    data-testid="tagesbericht-quick-yesterday"
                >
                    Gestern
                </button>
                <div className="flex-1" />
                <button
                    type="button"
                    onClick={() => void load(date)}
                    disabled={loading}
                    className="text-[11px] uppercase tracking-wider px-4 py-1.5 rounded-sm"
                    style={{
                        background: "#C48B34",
                        color: "white",
                        border: "1px solid #8A5A1D",
                        cursor: loading ? "wait" : "pointer",
                        opacity: loading ? 0.7 : 1,
                        fontWeight: 600,
                    }}
                    data-testid="tagesbericht-load-btn"
                >
                    {loading ? "Wird erstellt…" : "Bericht erstellen"}
                </button>
            </div>

            {error && (
                <div
                    className="p-4 rounded-sm mb-6 text-sm"
                    style={{ background: "hsl(0 50% 96%)", border: "1px solid hsl(0 30% 80%)", color: "hsl(0 30% 30%)" }}
                >
                    {error}
                </div>
            )}

            {report && (
                <article
                    className="p-6 md:p-10 rounded-sm"
                    style={{ background: "#FFFDF9", border: "1px solid hsl(var(--hf-line))" }}
                    data-testid="tagesbericht-content"
                >
                    <div className="font-display text-3xl md:text-4xl text-hf-ink mb-8" style={{ lineHeight: 1.15 }}>
                        Bericht vom {report.date_pretty}
                    </div>

                    {/* KURZ ZUSAMMENGEFASST */}
                    <section className="mb-10">
                        <div
                            className="text-[10px] uppercase tracking-[0.22em] mb-3 flex items-center gap-3"
                            style={{ color: "hsl(var(--hf-muted))" }}
                        >
                            <span aria-hidden style={{ flex: 1, height: 1, background: "hsl(var(--hf-line))" }} />
                            <span>Kurz zusammengefasst</span>
                            <span aria-hidden style={{ flex: 1, height: 1, background: "hsl(var(--hf-line))" }} />
                        </div>
                        <p
                            className="font-display text-lg md:text-xl"
                            style={{ color: moodColor, lineHeight: 1.55 }}
                        >
                            {report.summary}
                        </p>
                    </section>

                    {/* HIGHLIGHTS */}
                    {report.highlights.length > 0 && (
                        <section className="mb-10">
                            <h2 className="font-display text-xl text-hf-ink mb-3">Highlights des Tages</h2>
                            <ul className="space-y-1.5 text-base text-hf-ink">
                                {report.highlights.map((h, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <span aria-hidden className="mt-1 inline-block rounded-full" style={{ width: 5, height: 5, background: "#C48B34", flex: "0 0 auto" }} />
                                        <span>{h}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* ECHTE 360°-INTERAKTIONEN — nach Auto-Fire-Filter.
                     * Ein `viewer_open`-Event zählt nur dann, wenn in derselben
                     * Session für dieselbe Media-ID auch ein Folge-Event
                     * existiert (Play, Drag, Fullscreen etc.). Reines Mount-
                     * Rauschen der Home-Sektionen wird nicht mitgezählt. */}
                    {report.real_viewer_stats && (
                        <section className="mb-10" data-testid="tagesbericht-real-viewer-stats">
                            <h2 className="font-display text-xl text-hf-ink mb-3">
                                360°-Interaktionen (echt): {report.real_viewer_stats.total}
                            </h2>
                            <ul className="space-y-1.5 text-base text-hf-ink">
                                <li className="flex items-start gap-2">
                                    <span aria-hidden className="mt-1 inline-block rounded-full" style={{ width: 5, height: 5, background: "#C48B34", flex: "0 0 auto" }} />
                                    <span>davon Video360: {report.real_viewer_stats.video360}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span aria-hidden className="mt-1 inline-block rounded-full" style={{ width: 5, height: 5, background: "#C48B34", flex: "0 0 auto" }} />
                                    <span>davon Photo360: {report.real_viewer_stats.photo360}</span>
                                </li>
                                {report.real_viewer_stats.before_after_360 > 0 && (
                                    <li className="flex items-start gap-2">
                                        <span aria-hidden className="mt-1 inline-block rounded-full" style={{ width: 5, height: 5, background: "#C48B34", flex: "0 0 auto" }} />
                                        <span>davon Vorher/Nachher-360°: {report.real_viewer_stats.before_after_360}</span>
                                    </li>
                                )}
                            </ul>
                            {report.real_viewer_note && (
                                <p
                                    className="mt-3 text-xs italic"
                                    style={{ color: "hsl(var(--hf-muted))" }}
                                >
                                    {report.real_viewer_note}
                                </p>
                            )}
                        </section>
                    )}

                    {/* INTERESSANTE BESUCHE */}
                    {report.interesting.length > 0 && (
                        <section className="mb-10">
                            <h2 className="font-display text-xl text-hf-ink mb-4">Details der interessanten Besuche</h2>
                            <div className="space-y-5">
                                {report.interesting.map((s, i) => (
                                    <div key={i} className="border-l-2 pl-4 py-1" style={{ borderColor: "#C48B34" }}>
                                        <div className="text-sm font-semibold text-hf-ink mb-1">{s.heading}</div>
                                        <p className="text-hf-ink text-[15px]" style={{ lineHeight: 1.6 }}>{s.story}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* KURZBESUCHE */}
                    {report.short_visits_summary && (
                        <section className="mb-10">
                            <h2 className="font-display text-xl text-hf-ink mb-3">Kurzbesuche</h2>
                            <p className="text-hf-muted text-[15px]" style={{ lineHeight: 1.6 }}>
                                {report.short_visits_summary}
                            </p>
                        </section>
                    )}

                    {/* FORM-LINK */}
                    {report.form_submissions_count > 0 && (
                        <section className="mb-10">
                            <Link
                                to="/admin/anfragen"
                                className="inline-flex items-center gap-1 text-sm underline"
                                style={{ color: "#8A5A1D" }}
                            >
                                Zu den Anfragen
                                <ExternalLink size={12} strokeWidth={2} />
                            </Link>
                        </section>
                    )}

                    {/* FAZIT */}
                    {report.closing_lines.length > 0 && (
                        <section>
                            <div
                                className="text-[10px] uppercase tracking-[0.22em] mb-3 flex items-center gap-3"
                                style={{ color: "hsl(var(--hf-muted))" }}
                            >
                                <span aria-hidden style={{ flex: 1, height: 1, background: "hsl(var(--hf-line))" }} />
                                <span>Fazit</span>
                                <span aria-hidden style={{ flex: 1, height: 1, background: "hsl(var(--hf-line))" }} />
                            </div>
                            <div className="space-y-3 font-display text-[17px] text-hf-ink" style={{ lineHeight: 1.6 }}>
                                {report.closing_lines.map((c, i) => (
                                    <p key={i}>{c}</p>
                                ))}
                            </div>
                        </section>
                    )}
                </article>
            )}

            {!report && !loading && !error && (
                <p className="text-hf-muted text-sm">Wählen Sie ein Datum und klicken Sie auf „Bericht erstellen“.</p>
            )}
        </div>
    );
}
